/**
 * Trustpilot PII Sanitization + CSV-Injection Defense (Story 23.3 AC #5)
 *
 * Trustpilot review records contain reviewer PII by default:
 *  - `consumer.displayName`
 *  - `consumer.displayLocation`
 *  - `consumer.email` (private tier only)
 *  - review `title`, `text`
 *  - `companyReply.text`
 *  - `referralEmail` (private tier only)
 *  - `referenceId` (private tier only — customer-supplied; may embed PII)
 *  - `tags` (private tier only — may contain custom CRM data)
 *  - `findReviewer` object (private tier only)
 *
 * **Round 3 Red Team Attack #3** — `sanitizeTrustpilotPii(record)` MUST be
 * applied before any record enters `console.log`, Sentry breadcrumbs,
 * structured-log events, or queue-error payloads. The unredacted form is
 * in scope ONLY inside `fetchMetrics` → transformer → return path.
 *
 * **Round 3 Red Team Attack #4 — CSV-injection defense**.
 * `stripCsvInjectionPrefix(value)` strips leading `=`, `+`, `@`, `-`, `\t`,
 * `\r`, `\n` characters; LOOP-strips to handle cascades like `=+@cmd`.
 * Re-exported from CallRail's `pii.ts` (NOT redeclared) per the directive
 * intent — single source of truth.
 *
 * Co-located fuzz tests in `pii.test.ts` assert pathological inputs
 * (PII embedded in JSON-string structured keys, URL-encoded variants).
 */
import { stripCsvInjectionPrefix as stripCsvInjectionPrefixFromCallrail } from "../callrail/pii";

/**
 * Re-export the CallRail-shipped CSV-injection prefix stripper. AC #5
 * mandates REUSE (not redeclare) — the story spec text references "Moz
 * 20.7's `moz/normalize.ts`" but the actual exported helper lives in
 * `callrail/pii.ts`. Using the existing export proves the helper-reuse
 * contract (Story 23.3 Task 9.3 compile-time test imports this binding).
 */
export const stripCsvInjectionPrefix = stripCsvInjectionPrefixFromCallrail;

/**
 * Field names this redactor scrubs from log-bound records.
 * Maintained as a Set for O(1) checks during deep traversal.
 *
 * **Wire-format names (snake_case + camelCase mix)** — the redactor walks
 * arbitrary nested objects, so both shapes appear depending on whether the
 * record has gone through `transformers.ts` yet.
 */
export const TRUSTPILOT_PII_FIELDS: ReadonlySet<string> = new Set([
  // Reviewer fields (always-tier when fetched)
  "displayName",
  "displayLocation",
  "email",
  // Review body fields (always-tier)
  "title",
  "text",
  // Reply body
  "companyReplyText",
  // Private-tier additional PII fields (gated on `privateApiEntitled === true`)
  "referralEmail",
  "referenceId",
  "tags",
  // Round 2 fix R2-22 — `findReviewer` removed from blanket-redact set.
  // Per Story 35.3 AC #14: `findReviewer.profileUrl` is allowed (public
  // Trustpilot URL is NOT PII); only `findReviewer.displayName` and
  // `findReviewer.displayLocation` are PII. The blanket-redact would
  // redact the public profile URL too, which violates AC #14. The Set
  // already contains `displayName` and `displayLocation` literals, and
  // the recursive walker descends into nested objects matching field
  // names — so leaving `findReviewer` OFF the set lets the recursive
  // walker do surgical redaction on its nested fields.
  // Camel-case flattened forms emitted by transformers.ts
  "consumerDisplayName",
  "consumerDisplayLocation",
  "consumerEmail",
  // Story 35.3 — invitation recipient PII (both snake_case wire form +
  // camelCase flattened form). Per AC #14, the `template_name` /
  // `templateName` + `sentAt` are NOT PII (template names are
  // agency-controlled; sent timestamps are not PII). Only the
  // recipient email is redacted.
  "recipient_email",
  "recipientEmail",
]);

/**
 * The 8 private-tier-gated fields stripped from records when
 * `metadata.privateApiEntitled === false`. Exported as a constant per
 * AC #11 (`TRUSTPILOT_PRIVATE_FIELDS`).
 */
export const TRUSTPILOT_PRIVATE_FIELDS: ReadonlyArray<string> = [
  "referenceId",
  "referralEmail",
  "source",
  "status",
  "tags",
  "experiencedAt",
  "location",
  "findReviewer",
];

/**
 * Recursively redact PII fields in an arbitrary record. Returns a NEW
 * object (does not mutate input) — adapter code passes the unredacted
 * form through the data path, and only the log-emission call sites
 * pass through this helper.
 *
 * Implementation walks nested objects + arrays. PII field values are
 * replaced with the literal string `"[redacted]"` so the log payload
 * shape stays intact (downstream log parsers can still grep field names
 * for forensic value).
 */
export function sanitizeTrustpilotPii<T>(record: T): T {
  return redactDeep(record) as T;
}

function redactDeep(value: unknown): unknown {
  if (value === null || value === undefined) return value;
  if (typeof value !== "object") return value;
  if (Array.isArray(value)) {
    return value.map((v) => redactDeep(v));
  }
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
    if (TRUSTPILOT_PII_FIELDS.has(k)) {
      out[k] = "[redacted]";
    } else {
      out[k] = redactDeep(v);
    }
  }
  return out;
}
